import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Add, Close } from '@mui/icons-material';
import {
  type Control,
  type FieldArrayWithId,
  type FieldError,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
  Controller,
} from 'react-hook-form';

import { EMPTY_PRODUCT_OPTION } from '../../constants/voucher-product-form.constants';
import type { CreateVoucherRequest } from '../../types';
import { getFieldErrorMessage } from '../../utils/voucher-product-form.utils';
import { ProductPicker } from './product-picker';

interface PurchaseProductsFieldsetProps {
  control: Control<CreateVoucherRequest>;
  fields: FieldArrayWithId<CreateVoucherRequest, 'purchaseProducts', 'id'>[];
  append: UseFieldArrayAppend<CreateVoucherRequest, 'purchaseProducts'>;
  remove: UseFieldArrayRemove;
  fieldErrors?: (FieldError | { value?: FieldError } | undefined)[];
  errorMessage?: string;
  disabled?: boolean;
  onProductChange?: (
    newProduct?: { value: string; label: string; division?: string } | null
  ) => void;
}

export const PurchaseProductsFieldset = ({
  control,
  fields,
  append,
  remove,
  fieldErrors,
  errorMessage,
  disabled = false,
  onProductChange,
}: PurchaseProductsFieldsetProps) => {
  const hasError = Boolean(errorMessage);

  return (
    <FormControl isInvalid={hasError}>
      <VStack spacing={4} align="stretch">
        {fields.map((field, index) => {
          const fieldErrorMessage = getFieldErrorMessage(fieldErrors?.[index]);
          const isLastItem = index === fields.length - 1;
          const showRemoveButton = !disabled && fields.length > 1;
          const showAddButton = !disabled && isLastItem;

          return (
            <Box key={field.id}>
              <FormControl isInvalid={Boolean(fieldErrorMessage)}>
                <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                  Nama produk
                </FormLabel>
                <HStack spacing={3} align="flex-end">
                  <Box flex={1}>
                    <Controller
                      control={control}
                      name={`purchaseProducts.${index}` as const}
                      render={({ field: controllerField }) => (
                        <ProductPicker
                          debounce={500}
                          value={
                            controllerField.value?.value
                              ? controllerField.value
                              : null
                          }
                          onChange={(option) => {
                            // Cast to access division from ProductSelectOption
                            const optionWithDivision = option as {
                              value: string;
                              label: string;
                              division?: string;
                            } | null;
                            const nextValue = optionWithDivision
                              ? {
                                  value: optionWithDivision.value,
                                  label: optionWithDivision.label,
                                  division: optionWithDivision.division,
                                }
                              : { ...EMPTY_PRODUCT_OPTION };

                            controllerField.onChange(nextValue);

                            // Trigger reward product sync when purchase product changes
                            // Pass the new product directly to avoid stale closure
                            if (onProductChange) {
                              onProductChange(optionWithDivision);
                            }
                          }}
                          isDisabled={disabled}
                        />
                      )}
                    />
                  </Box>
                  {showRemoveButton && (
                    <IconButton
                      aria-label="Hapus produk"
                      icon={<Close fontSize="small" />}
                      size="sm"
                      variant="ghost"
                      color="gray.400"
                      _hover={{ bg: 'gray.100', color: 'gray.600' }}
                      onClick={() => {
                        remove(index);
                        // Trigger reward product sync when purchase product is removed
                        if (onProductChange) {
                          // Use setTimeout to ensure the removal is processed first
                          setTimeout(() => {
                            onProductChange();
                          }, 0);
                        }
                      }}
                    />
                  )}
                  {showAddButton && (
                    <IconButton
                      aria-label="Tambah produk"
                      icon={<Add fontSize="small" />}
                      size="sm"
                      variant="outline"
                      borderColor="blue.500"
                      color="blue.500"
                      borderRadius="md"
                      _hover={{ bg: 'blue.50' }}
                      onClick={() =>
                        append(
                          { ...EMPTY_PRODUCT_OPTION },
                          { shouldFocus: false }
                        )
                      }
                    />
                  )}
                </HStack>
                {fieldErrorMessage && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {fieldErrorMessage}
                  </Text>
                )}
              </FormControl>
            </Box>
          );
        })}
      </VStack>
      {hasError && <FormErrorMessage mt={2}>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
};
